import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FileText, BarChart3, CheckCircle, TrendingUp, Target, Clock, BookOpen, Sparkles, Play } from 'lucide-react';
import { LoadingSpinner } from '../components/LoadingSpinner';
import LandingStyleHero from '../components/productHero/LandingStyleHero';
import LaunchAnnouncementBanner from '../components/LaunchAnnouncementBanner';
import ProductDemoVideoEmbed from '../components/video/ProductDemoVideoEmbed';
import { getWalkthroughVideo } from '../config/walkthroughVideos';
import SEO from '../components/SEO';
import PremiumAccessPrompt from '../components/PremiumAccessPrompt';
import { usePremiumProductAccess } from '../hooks/usePremiumProductAccess';
import { PREMIUM_PRODUCT_KEYS } from '../config/premiumProducts';

const sectionTitleClass =
  'text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight bg-gradient-to-r from-slate-900 via-slate-700 to-slate-900 bg-clip-text text-transparent';

const glassCardClass =
  'rounded-2xl border border-slate-200/80 bg-white/95 shadow-lg shadow-slate-200/50 p-5 sm:p-6 md:p-8 transition-all duration-300 hover:shadow-xl hover:border-emerald-200/60 hover:-translate-y-0.5';

const whatYouGet = [
  { icon: FileText, title: 'A timed Casey-style case', text: 'Work through a full Casey-style flow with the same kind of pressure, pacing, and question variety you\'ll face on test day.' },
  { icon: BarChart3, title: 'Exhibits, numerics, and short answers', text: 'Practice reading charts, choosing the right answer fast, and explaining your thinking clearly when the case gets more detailed.' },
  { icon: CheckCircle, title: 'A coach summary you can actually use', text: 'Finish with a clear report on strengths, missed points, correct answers, and what to do differently on your next attempt.' },
];

const howItWorks = [
  { icon: FileText, title: 'Start the case and run the timer', text: 'Begin a Casey-style case and move through the questions in one sitting so your pacing feels real, not theoretical.' },
  { icon: BarChart3, title: 'Unlock the case as you go', text: 'Review exhibits, answer numerics, and respond to short-answer prompts as new information appears.' },
  { icon: CheckCircle, title: 'See exactly where you lost points', text: 'Each step shows how you performed so you can spot weak areas instead of guessing what went wrong.' },
  { icon: TrendingUp, title: 'Retake with a better plan', text: 'Use the final report to focus your next rep on the part that will move your score the most.' },
];

const whyCandidates = [
  { icon: Target, title: 'Practice under pressure', text: 'It is easier to think clearly in the real Casey when the format no longer feels new.' },
  { icon: Clock, title: 'Turn one attempt into a learning loop', text: 'You do not just get a score. You get a clearer view of how you handled the case and what to improve next.' },
  { icon: BookOpen, title: 'Build confidence with real reps', text: 'The best prep is not reading about the format. It is sitting in it, making decisions, and getting useful feedback afterward.' },
];

const relatedLinks = [
  { label: 'BCG Candidate-Led AI Coach', path: '/bcg-ai-coach', Icon: Sparkles },
  { label: 'BCG Casey Guide', path: '/bcg-casey-guide', Icon: BookOpen },
  { label: 'About BCG Casey', path: '/about-bcg-casey', Icon: FileText },
];

function CaseyProofSection({ onStart, checkingAccess }) {
  const [starting, setStarting] = useState(false);
  const video = getWalkthroughVideo('casey2026');

  const handleStartCasey = () => {
    onStart(() => setStarting(true));
  };

  return (
    <section id="casey-proof" className="py-20 sm:py-28 bg-white px-4 sm:px-8 md:px-12 lg:px-16">
      <div className="max-w-[120rem] mx-auto">
        <h2 className={sectionTitleClass + ' text-center mb-8 sm:mb-10'}>What your feedback actually shows</h2>
        <p className="text-center text-gray-600 max-w-2xl mx-auto mb-12 sm:mb-14 text-sm sm:text-base">
          Each run gives you something you can use on the next one: where your structure held, where your math slipped, how strong your synthesis was, and the next move that would improve the case most.
        </p>
        <div className="grid grid-cols-1 lg:grid-cols-[1.25fr_0.9fr] gap-8 lg:gap-10 items-center">
          <div>
            <div className="rounded-2xl border border-slate-200/80 bg-white/95 shadow-xl overflow-hidden">
            <ProductDemoVideoEmbed videoKey="casey2026" />
            <div className="px-4 py-2.5 border-t border-slate-100 bg-white">
              <div className="text-sm font-semibold text-gray-900">{video.title}</div>
              <div className="text-xs text-gray-500 mt-0.5">{video.description}</div>
            </div>
            </div>
          </div>
          <div className="space-y-3">
            {[
              { label: 'Structure', text: 'See whether your branches made the analysis clear and usable.' },
              { label: 'Math', text: 'See whether your numbers supported the recommendation.' },
              { label: 'Synthesis', text: 'See whether you landed the answer clearly.' },
              { label: 'Next move', text: 'Know exactly what to change on the next rep.' },
            ].map((item, i) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="flex gap-3 rounded-xl border border-slate-200/80 bg-white p-5 text-left"
              >
                <span className="text-emerald-600 font-semibold text-base shrink-0">{item.label}:</span>
                <span className="text-gray-600 text-base">{item.text}</span>
              </motion.div>
            ))}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.24 }}
              className="pt-2"
            >
              <button
                type="button"
                onClick={handleStartCasey}
                disabled={starting || checkingAccess}
                className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-blue-400 px-5 py-3 text-sm font-semibold text-white shadow-md hover:shadow-lg transition w-full sm:w-auto justify-center disabled:opacity-80 disabled:pointer-events-none min-w-[180px]"
              >
                {starting || checkingAccess ? (
                  <>
                    <LoadingSpinner size="sm" tone="onPrimary" className="shrink-0" /> {checkingAccess ? 'Checking access…' : 'Starting…'}
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4" strokeWidth={2} /> Start BCG Casey Simulation
                  </>
                )}
              </button>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CaseyPreviewPanel() {
  return (
    <>
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(2,6,23,0.08)_0%,rgba(5,22,16,0.12)_100%)]" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-emerald-400/50 to-transparent" />
      <div className="relative border-b border-emerald-500/20 p-4 text-white bg-slate-900/40">
        <div className="flex items-center justify-between">
          <span className="text-xs font-bold text-emerald-200/90">BCG Casey</span>
          <span className="rounded-full bg-emerald-500/20 px-2.5 py-1 text-[11px] font-semibold text-emerald-200">Question 4 of 8</span>
        </div>
      </div>
      <div className="relative p-4">
        <div className="rounded-2xl border border-white/15 overflow-hidden bg-white/5 backdrop-blur-sm ring-1 ring-white/5 shadow-[0_16px_36px_-24px_rgba(2,6,23,0.78)]">
          <img
            src="/images/landing/casey-in-session.png"
            alt="BCG Casey in-session — question, exhibits, and feedback"
            className="w-full h-auto block"
          />
        </div>
      </div>
    </>
  );
}

export default function BCGCaseySimulationPage() {
  const navigate = useNavigate();
  const caseyAccess = usePremiumProductAccess(PREMIUM_PRODUCT_KEYS.caseySimulation);

  const handleScrollToProof = () => document.getElementById('casey-proof')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const handleScrollToWalkthrough = () => document.getElementById('casey-proof')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const handleViewPricing = () => navigate('/', { state: { scrollToPricing: true } });
  const handleSeeDemo = () => navigate('/', { state: { scrollToLiveDemos: true, track: 'bcg', demoVariantBcg: 'assessment' } });
  const handleStartCasey = (onAllowed) => {
    caseyAccess.attemptAccess(() => {
      onAllowed();
      navigate('/bcg-casey-app');
    }, {
      returnTo: '/bcg-casey-app',
      onDemo: handleSeeDemo,
    });
  };

  return (
    <>
      <SEO
        title="BCG Casey Simulation | Cognidoor"
        description="Practice the full BCG Casey assessment with timed questions, exhibits, and a Coach Summary. Deterministic scoring and realistic flow."
        url="/bcg-casey-simulation"
      />
      <div className="bg-white text-gray-900 font-sans" style={{ '--cog-blue-1': '#277AB2', '--cog-blue-2': '#0B5996', '--cog-blue-3': '#082B4A' }}>
        <LandingStyleHero
          track="bcg"
          headline="Practice the BCG Casey before your real attempt."
          subheadline="Run a Casey-style case with timed questions, exhibits, short answers, and a final coach summary that shows what you did well, where you lost points, and what to fix before test day."
          eyebrowChips={['Timed Casey-style run', 'Exhibits + numerics', 'Final coach summary']}
          proofRow={[
            { value: 'Built around the Casey format' },
            { value: 'Clear step-by-step scoring' },
            { value: 'Retake with a sharper plan' },
          ]}
          primaryCtaLabel="Start Casey simulation"
          onPrimaryCta={handleScrollToProof}
          secondaryCtaLabel="Watch walkthrough"
          onSecondaryCta={handleScrollToWalkthrough}
          announcement={
            <LaunchAnnouncementBanner
              track="bcg"
              badge="New Casey experience"
              title="A cleaner Casey run with feedback that teaches the next move."
              body="The updated flow is easier to follow, faster to review, and clearer about where your structure, math, evidence, and recommendation started to drift."
              primaryCta="Try Casey"
              onPrimaryCta={handleScrollToProof}
            />
          }
          previewChildren={<CaseyPreviewPanel />}
        />

        <section id="what-you-get" className="py-14 sm:py-20 bg-gradient-to-b from-white via-slate-50/50 to-white text-center px-4 sm:px-6 md:px-10">
          <h2 className={sectionTitleClass + ' mb-10 sm:mb-14'}>What you get from one Casey run</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {whatYouGet.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className={glassCardClass + ' text-left'}
              >
                <item.icon className="w-8 h-8 text-emerald-600 mb-3" strokeWidth={1.8} />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </section>

        <CaseyProofSection
          onStart={handleStartCasey}
          checkingAccess={caseyAccess.isLoading}
        />

        <section className="py-14 sm:py-20 bg-white text-center px-4 sm:px-6 md:px-10">
          <h2 className={sectionTitleClass + ' mb-10 sm:mb-14'}>How it works</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {howItWorks.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className={glassCardClass + ' text-left'}
              >
                <item.icon className="w-8 h-8 text-emerald-600 mb-3" strokeWidth={1.8} />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </section>

        <section className="py-14 sm:py-20 bg-gradient-to-b from-white via-slate-50/50 to-white text-center px-4 sm:px-6 md:px-10">
          <h2 className={sectionTitleClass + ' mb-10 sm:mb-14'}>Why candidates use this before test day</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {whyCandidates.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className={glassCardClass + ' text-left'}
              >
                <item.icon className="w-8 h-8 text-emerald-600 mb-3" strokeWidth={1.8} />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </section>

        <section className="py-14 sm:py-20 bg-white text-center px-4 sm:px-6 md:px-10">
          <h2 className={sectionTitleClass + ' mb-8 sm:mb-12'}>More BCG prep</h2>
          <div className="flex flex-wrap justify-center gap-4 max-w-3xl mx-auto">
            {relatedLinks.map(({ label, path, Icon }) => (
              <motion.button
                key={path}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate(path)}
                className="inline-flex items-center gap-2 rounded-xl border-2 border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-gray-700 shadow-sm hover:border-emerald-300 hover:bg-emerald-50/50 hover:text-emerald-800 transition-colors"
              >
                <Icon className="w-4 h-4" strokeWidth={2} /> {label}
              </motion.button>
            ))}
          </div>
          <p className="mt-8 text-sm text-gray-500">
            Casey Simulation is best when paired with live BCG-style interview practice.{' '}
            <button type="button" onClick={handleViewPricing} className="font-semibold text-emerald-600 hover:text-emerald-700 underline underline-offset-2">
              See plans and pricing
            </button>
          </p>
        </section>
      </div>
      <PremiumAccessPrompt {...caseyAccess.promptProps} />
    </>
  );
}
